import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useUi } from '../context/UiContext';

const CRITERIA_FIELDS = [
  ['valid_phone_number', 'Valid Phone Number'],
  ['phone_reachable', 'Phone number reachable'],
  ['correct_location', 'Correct location / serviceable area'],
  ['matches_customer_profile', 'Matches target customer profile'],
  ['relevant_requirement', 'Relevant requirement'],
  ['actual_requirement', 'Actual requirement / need'],
  ['suitable_timeline_budget', 'Suitable purchase timeline / budget'],
  ['shows_interest', 'Shows interest'],
  ['asks_relevant_questions', 'Asks relevant questions'],
  ['appointment_scheduled', 'Appointment / meeting scheduled'],
];

const STATUS_RANK = { 'Fully Met': 2, 'Partially Met': 1, 'Not Met': 0 };

export default function MonthlyUploadDetail() {
  const { brandId, uploadId } = useParams();
  const navigate = useNavigate();
  const { showToast, confirmAction } = useUi();

  const [detail, setDetail] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [sortField, setSortField] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const sortedLeads = useMemo(() => {
    if (!detail) return [];
    let leads = detail.leads;
    if (sortField && statusFilter) {
      leads = leads.filter((lead) => lead[sortField] === statusFilter);
    }
    if (sortField) {
      leads = [...leads].sort((a, b) => STATUS_RANK[b[sortField]] - STATUS_RANK[a[sortField]]);
    }
    return leads;
  }, [detail, sortField, statusFilter]);

  useEffect(() => {
    api.getMonthlyUpload(uploadId).then(setDetail).catch(() => navigate(`/lead-quality-score/brands/${brandId}`));
  }, [uploadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteUpload = async () => {
    const ok = await confirmAction(
      'Delete Monthly Results',
      `Are you sure you want to delete the ${detail.month} results (${detail.total_leads} leads)? The ${detail.month} folder in Google Drive will also be moved to the Drive trash.`
    );
    if (!ok) return;
    try {
      await api.deleteMonthlyUpload(uploadId);
    } catch (err) {
      showToast(err.detail || 'Could not delete the monthly results.', 'error');
      return;
    }
    showToast('Monthly results deleted.', 'info');
    navigate(`/lead-quality-score/brands/${brandId}`);
  };

  if (!detail) return null;

  return (
    <div className="container page-content">
      <div className="page-header">
        <div className="brand-header-top">
          <div className="brand-header-left">
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/lead-quality-score/brands/${brandId}`)}>
              <BackIcon /> Back to Brand
            </button>
          </div>
          <h2 className="brand-header-title">{detail.month} — {detail.overall_percentage}%</h2>
          <div className="page-header-actions brand-header-actions">
            <button className="btn btn-danger btn-sm" onClick={deleteUpload}>Delete</button>
          </div>
        </div>
      </div>

      <div className="section-header">
        <div>
          <h3 className="section-title">{detail.filename}</h3>
          <p className="section-subtitle">
            {detail.total_leads} leads
          </p>
          {detail.google_drive_status === 'saved' && (
            <p style={{ fontSize: '0.78rem', color: 'var(--green)', marginTop: 4 }}>
              ✓ Saved to Google Drive
              {detail.google_drive_file_url && (
                <>
                  {' · '}
                  <a href={detail.google_drive_file_url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    Open in Drive
                  </a>
                </>
              )}
            </p>
          )}
          {detail.google_drive_status === 'failed' && (
            <p style={{ fontSize: '0.78rem', color: 'var(--red)', marginTop: 4 }}>
              ⚠ Not saved to Google Drive
            </p>
          )}
        </div>
        <div>
          <div className="flex gap-12">
            <select
              className="form-control"
              style={{ maxWidth: 260 }}
              value={sortField}
              onChange={(e) => { setSortField(e.target.value); setStatusFilter(''); }}
            >
              <option value="">Criteria</option>
              {CRITERIA_FIELDS.map(([field, label]) => (
                <option key={field} value={field}>{label}</option>
              ))}
            </select>
            <select
              className="form-control"
              style={{ maxWidth: 160 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={!sortField}
            >
              <option value="">Status</option>
              <option value="Fully Met">Fully Met</option>
              <option value="Partially Met">Partially Met</option>
              <option value="Not Met">Not Met</option>
            </select>
          </div>
          {sortField && (
            <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-faint)', marginTop: 8, textAlign: 'right' }}>
              {sortedLeads.length} of {detail.total_leads}
            </p>
          )}
        </div>
      </div>

      <div className="leads-section">
        <div className="leads-table-wrap">
          <table className="leads-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Name</th>
                <th>Score</th>
                <th>Percentage</th>
                {sortField && <th>Criteria Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeads.map((lead, index) => (
                <tr key={lead.id} onClick={() => setActiveLead(lead)}>
                  <td>{index + 1}</td>
                  <td><div className="lead-name-cell"><div className="name">{lead.name}</div></div></td>
                  <td>
                    <span style={{ fontWeight: 700 }}>{lead.total_score}</span>
                    <span style={{ color: 'var(--color-ink-faint)', fontSize: '0.75rem' }}>/100</span>
                  </td>
                  <td>{lead.percentage}%</td>
                  {sortField && (
                    <td>
                      <span className={`badge badge-${statusClass(lead[sortField])}`}>{lead[sortField]}</span>
                    </td>
                  )}
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setActiveLead(lead); }}>
                      View Breakdown
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {activeLead && (
        <div className="modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setActiveLead(null); }}>
          <div className="modal">
            <div className="modal-header">
              <div>
                <div className="modal-title">{activeLead.name}</div>
                <div className="modal-subtitle">{activeLead.total_score}/100 · {activeLead.percentage}%</div>
              </div>
              <button className="modal-close" onClick={() => setActiveLead(null)}>✕</button>
            </div>
            <div className="modal-body">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Criteria</th>
                    <th>Status</th>
                    <th>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {activeLead.breakdown.map((item) => (
                    <tr key={item.criteria}>
                      <td style={{ fontSize: '0.85rem' }}>{item.criteria}</td>
                      <td><span className={`badge badge-${statusClass(item.status)}`}>{item.status}</span></td>
                      <td style={{ fontWeight: 700 }}>{item.points}/10</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setActiveLead(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function statusClass(status) {
  if (status === 'Fully Met') return 'high';
  if (status === 'Partially Met') return 'qualified';
  return 'invalid';
}

function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
